import { cn } from '@seen/ui'
import type {
  AriaRole,
  FocusEventHandler,
  JSX,
  KeyboardEventHandler,
  MouseEventHandler,
  PointerEventHandler,
  ReactNode,
  Ref,
} from 'react'
import { CONFIGURING_NODE_CLASS } from '#src/components/rich-text-editor/conditional-display/useIsNodeConfiguring.ts'

export function ObservationWrapper({
  children,
  onClick,
  className,
  noHover,
  ref,
  role,
  tabIndex,
  onKeyDown,
  onFocus,
  onBlur,
  onPointerDownCapture,
  onMouseDownCapture,
  engaged,
  configuring,
}: {
  children: ReactNode
  onClick?: MouseEventHandler<HTMLSpanElement>
  className?: string
  noHover?: boolean
  ref?: Ref<HTMLSpanElement>
  role?: AriaRole
  tabIndex?: number
  onKeyDown?: KeyboardEventHandler<HTMLSpanElement>
  onFocus?: FocusEventHandler<HTMLSpanElement>
  onBlur?: FocusEventHandler<HTMLSpanElement>
  onPointerDownCapture?: PointerEventHandler<HTMLSpanElement>
  onMouseDownCapture?: MouseEventHandler<HTMLSpanElement>
  // Marked for `group-data-[engaged=true]` styling.
  engaged?: boolean
  // Open in the configuration panel beside the editor.
  configuring?: boolean
}): JSX.Element {
  return (
    <span
      onClick={onClick}
      onKeyDown={onKeyDown}
      onFocus={onFocus}
      onBlur={onBlur}
      onPointerDownCapture={onPointerDownCapture}
      onMouseDownCapture={onMouseDownCapture}
      data-engaged={engaged ? 'true' : undefined}
      data-configuring={configuring ? 'true' : undefined}
      role={role}
      tabIndex={tabIndex}
      className={cn(
        // Same ground as the vitals nodes (ObservationNodeFrame).
        'flex select-none flex-wrap gap-y-0.5 rounded-xs bg-frame box-decoration-clone px-1 py-px hover:bg-frame-hover group-[.ProseMirror-selectednode]:bg-[lch(53_52.26_286.91_/_0.4)]',
        noHover && 'hover:bg-frame',
        configuring && CONFIGURING_NODE_CLASS,
        // A required question left unanswered at a sign or submit (see
        // RequiredAnswersExtension, which marks the node).
        '[[data-required-gap]_&]:ring-2 [[data-required-gap]_&]:ring-destructive/60 [[data-required-gap]_&]:ring-offset-1',
        className
      )}
      ref={ref}
    >
      {children}
    </span>
  )
}

export function ObservationNoValue({ label }: { label: string }): JSX.Element {
  return (
    <ObservationWrapper noHover>
      <span>{label}:&nbsp;</span>
      <span className="text-foreground-secondary">no value</span>
    </ObservationWrapper>
  )
}
